import { Fragment, useMemo, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  api,
  type KillGrowthRange,
  type SelfReportRank,
  type SelfReportRankNeighbor,
  type SelfReportSummary,
} from '@/lib/api';
import { cn } from '@/lib/utils';
import { formatClassRange, formatGrowthPct, KILL_GROWTH_CLASS_META } from '@/lib/killGrowth';
import PlayerHubHeader from '@/components/PlayerHubHeader';
import StrengthGrowthCard from '@/components/StrengthGrowthCard';
import VsPowerCard from '@/components/VsPowerCard';
import CombatEfficiencyCard from '@/components/CombatEfficiencyCard';
import { formatKills } from './SelfReport';

export default function PlayerHubStats() {
  const params = useParams<{ token: string }>();
  const token = params.token ?? '';
  // Same query key as the Report page, so switching tabs reuses the cache.
  const summaryQuery = useQuery({
    queryKey: ['self-report', token],
    queryFn: () => api.getSelfReportSummary(token),
    retry: false,
    enabled: !!token,
  });
  const [metric, setMetric] = useState<MetricKey>('mSum');

  if (summaryQuery.isLoading) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100">
        <div className="mx-auto max-w-md px-4 py-10 text-slate-400">Loading…</div>
      </div>
    );
  }
  if (summaryQuery.isError || !summaryQuery.data) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100">
        <div className="mx-auto max-w-md px-4 py-10">
          <h1 className="mb-2 text-xl font-semibold">Link not valid</h1>
          <p className="text-sm text-slate-400">
            This link is invalid or has been revoked. Please ask an R4/R5 to
            send you a fresh one.
          </p>
        </div>
      </div>
    );
  }

  const summary = summaryQuery.data;
  const { player, history, ranks, comparison } = summary;
  const neighbors = comparison.neighbors[metric];
  const hasChase = !!(neighbors.prey || neighbors.hunter);
  const hasBuster =
    summary.busterDay != null || summary.busterRanking.entries.length > 0;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <PlayerHubHeader
        token={token}
        playerLabel={player.displayName?.trim() || player.name}
        playerRank={player.rank}
        subtitle="Stats"
      />

      <main className="mx-auto max-w-md space-y-4 px-4 py-6">
        {hasBuster && (
          <BusterDaySection
            busterDay={summary.busterDay}
            ranking={summary.busterRanking}
          />
        )}
        {hasChase && (
          <NeighborsSection
            neighbors={neighbors}
            metric={metric}
            quips={comparison.chaseQuips}
          />
        )}
        {ranks && <RankTiles ranks={ranks} />}
        <StrengthGrowthCard token={token} />
        <VsPowerCard token={token} />
        <KillGrowthSection token={token} />
        <CombatEfficiencyCard token={token} />
        <KillsChartSection history={history} />
        {comparison.distribution.mSum.length > 1 && (
          <DistributionSection
            comparison={comparison}
            metric={metric}
            onMetricChange={setMetric}
          />
        )}
        {comparison.trend.some((t) => t.median != null) && (
          <TrendSection trend={comparison.trend} />
        )}
        {history.length > 1 && <MTrendSection rows={history} />}
      </main>
    </div>
  );
}

const BUSTER_PODIUM = 3;

function BusterDaySection({
  busterDay,
  ranking,
}: {
  busterDay: SelfReportSummary['busterDay'];
  ranking: SelfReportSummary['busterRanking'];
}) {
  const [expanded, setExpanded] = useState(false);
  const { entries } = ranking;
  const own = entries.find((e) => e.isYou);
  const maxDelta = entries[0]?.deltaKills ?? 0;
  // Collapsed: podium plus the player directly ahead of and behind you.
  const visible = expanded
    ? entries
    : entries.filter(
        (e) =>
          e.position <= BUSTER_PODIUM ||
          (own != null && Math.abs(e.position - own.position) <= 1),
      );
  const canExpand = visible.length < entries.length || expanded;
  return (
    <div className="space-y-2">
      <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-400">
        Buster Day
      </h2>
      {busterDay && (
        <div className="rounded-lg border border-amber-800/60 bg-amber-950/25 px-4 py-3">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-amber-300">
            {busterDay.fallback
              ? 'Kills since previous snapshot'
              : 'Before vs. after Saturday'}
          </div>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="text-2xl font-semibold tabular-nums text-amber-100">
              {busterDay.deltaKills > 0 ? '+' : busterDay.deltaKills < 0 ? '-' : ''}
              {formatKills(Math.abs(busterDay.deltaKills))} M
            </span>
            <span className="text-xs text-amber-200/70">kills</span>
          </div>
          <div className="mt-0.5 text-[11px] text-slate-400 tabular-nums">
            {busterDay.previousDate ?? '—'} ·{' '}
            {formatKills(busterDay.previousKills)} → {busterDay.date} ·{' '}
            {formatKills(busterDay.currentKills)} M
          </div>
        </div>
      )}
      {entries.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-slate-800 bg-slate-900/40">
          <div className="flex items-baseline justify-between gap-2 border-b border-slate-800 px-3 py-2">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
              Alliance ranking
            </span>
            <span className="text-[10px] tabular-nums text-slate-500">
              {formatSaturday(ranking.saturdayIso)} · {ranking.entries.length}{' '}
              player{ranking.entries.length === 1 ? '' : 's'}
            </span>
          </div>
          <ol className="space-y-1 p-2">
            {visible.map((e, i) => {
              const prev = visible[i - 1];
              const gap = prev ? e.position - prev.position - 1 : 0;
              return (
                <Fragment key={e.player.id}>
                  {gap > 0 && (
                    <li>
                      <button
                        type="button"
                        onClick={() => setExpanded(true)}
                        className="w-full py-0.5 text-center text-[10px] text-slate-600 hover:text-slate-400"
                      >
                        <span className="tracking-[0.3em]">···</span> {gap} more
                      </button>
                    </li>
                  )}
                  <BusterRow entry={e} max={maxDelta} />
                </Fragment>
              );
            })}
          </ol>
          {canExpand && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="flex w-full items-center justify-center gap-1 border-t border-slate-800 py-1.5 text-[11px] text-slate-400 hover:bg-slate-800/40 hover:text-slate-200"
            >
              {expanded ? (
                <>
                  <ChevronUp size={12} /> Show less
                </>
              ) : (
                <>
                  <ChevronDown size={12} /> Show all {entries.length}
                </>
              )}
            </button>
          )}
          <div className="border-t border-slate-800 px-3 py-2 text-[11px] text-slate-400">
            <BusterFooter entries={ranking.entries} own={own ?? null} />
          </div>
        </div>
      )}
    </div>
  );
}

type BusterEntry = SelfReportSummary['busterRanking']['entries'][number];

function busterName(e: BusterEntry) {
  return e.player.displayName?.trim() || e.player.name;
}

function formatSaturday(iso: string) {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

const MEDAL_CLASSES: Record<number, string> = {
  1: 'border-yellow-400/70 bg-yellow-500/20 text-yellow-200',
  2: 'border-slate-300/60 bg-slate-300/15 text-slate-100',
  3: 'border-orange-500/60 bg-orange-700/25 text-orange-200',
};

const BAR_CLASSES: Record<number, string> = {
  1: 'bg-yellow-400/80',
  2: 'bg-slate-300/70',
  3: 'bg-orange-500/70',
};

function BusterRow({ entry, max }: { entry: BusterEntry; max: number }) {
  const medal = MEDAL_CLASSES[entry.position];
  const pct = max > 0 ? Math.max(3, (entry.deltaKills / max) * 100) : 0;
  return (
    <li
      className={cn(
        'flex items-center gap-2.5 rounded-md px-2 py-1.5',
        entry.isYou
          ? 'bg-amber-900/30 ring-1 ring-amber-600/60'
          : medal
            ? 'bg-slate-800/40'
            : '',
      )}
    >
      <span
        className={cn(
          'inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-[11px] font-bold tabular-nums',
          medal ?? 'border-slate-700 bg-slate-900 text-slate-400',
        )}
      >
        {entry.position}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <span
            className={cn(
              'min-w-0 truncate',
              medal ? 'text-sm font-semibold' : 'text-xs',
              entry.isYou ? 'text-amber-100' : 'text-slate-200',
            )}
          >
            {busterName(entry)}
            {entry.isYou && (
              <span className="ml-1.5 rounded bg-amber-600/30 px-1 py-px text-[9px] font-semibold uppercase tracking-wider text-amber-200">
                you
              </span>
            )}
          </span>
          <span
            className={cn(
              'shrink-0 tabular-nums',
              medal ? 'text-sm font-semibold text-slate-100' : 'text-xs text-slate-300',
            )}
          >
            +{formatKills(entry.deltaKills)}
            <span className="ml-0.5 text-[10px] font-normal text-slate-500">M</span>
          </span>
        </div>
        <div className="mt-1 h-1 overflow-hidden rounded-full bg-slate-800">
          <div
            className={cn(
              'h-full rounded-full',
              entry.isYou ? 'bg-amber-400' : (BAR_CLASSES[entry.position] ?? 'bg-rose-500/60'),
            )}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
    </li>
  );
}

function BusterFooter({
  entries,
  own,
}: {
  entries: BusterEntry[];
  own: BusterEntry | null;
}) {
  if (!own) {
    return (
      <>
        You're not ranked this week — it needs a kill snapshot before and
        after Saturday.
      </>
    );
  }
  if (own.position === 1) {
    return (
      <>
        <span aria-hidden>🏆</span>{' '}
        <span className="text-amber-200">You topped the alliance</span> this
        Buster Day.
      </>
    );
  }
  const ahead = entries[own.position - 2];
  return (
    <>
      You're <span className="font-semibold text-slate-200">#{own.position}</span>
      {ahead && (
        <>
          {' '}— <span className="tabular-nums text-slate-200">
            {formatKills(ahead.deltaKills - own.deltaKills)} M
          </span>{' '}
          behind {busterName(ahead)}.
        </>
      )}
    </>
  );
}

function KillGrowthSection({ token }: { token: string }) {
  const [range, setRange] = useState<KillGrowthRange>('90d');
  const q = useQuery({
    queryKey: ['player-kill-growth', token, range],
    queryFn: () => api.getSelfReportKillGrowth(token, range),
    enabled: !!token,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
  const data = q.data;
  const own = data?.own ?? null;
  const meta = own ? KILL_GROWTH_CLASS_META[own.class] : null;
  const chartData =
    data && own
      ? data.samples.map((iso, i) => ({
          label: iso.slice(5, 10),
          // Indexed (start = 100) → plain growth in %.
          You: own.indexed[i] - 100,
          Class:
            data.classMedian?.indexed[i] != null ? data.classMedian.indexed[i]! - 100 : null,
        }))
      : [];
  const beatsMedian =
    own && data?.classMedian?.medianPct != null
      ? own.pct - data.classMedian.medianPct
      : null;

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-400">
          Kill growth
        </h2>
        <div className="inline-flex rounded border border-slate-800 bg-slate-950 p-0.5 text-[11px]">
          {(['30d', '90d'] as const).map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setRange(r)}
              className={cn(
                'rounded px-2 py-0.5',
                range === r ? 'bg-slate-700 text-white' : 'text-slate-400',
              )}
            >
              {r}
            </button>
          ))}
        </div>
      </div>
      <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
        {q.isLoading ? (
          <div className="text-xs text-slate-500">Loading…</div>
        ) : !own || !meta || !data ? (
          <p className="text-xs text-slate-400">
            Not enough kill reports in this range yet. Update your kill count on
            the Report tab regularly — after a few weeks you'll see how you
            grow compared to players of your size.
          </p>
        ) : (
          <>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div
                  className="text-3xl font-semibold tabular-nums"
                  style={{ color: meta.color }}
                >
                  {own.baselineEstimatedFrom ? '≈' : ''}
                  {formatGrowthPct(own.pct)}
                </div>
                <div className="text-xs text-slate-400 tabular-nums">
                  +{formatKills(own.deltaM)} M kills · {formatKills(own.startKills)}{' '}
                  → {formatKills(own.endKills)} M
                </div>
              </div>
              <div className="text-right">
                <span className={cn('rounded border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider', meta.chip)}>
                  {meta.label}
                </span>
                <div className="mt-1 text-[10px] text-slate-500">{meta.sub}</div>
                {data.classMedian &&
                  formatClassRange(data.classMedian.minMSum, data.classMedian.maxMSum) && (
                    <div className="text-[10px] tabular-nums text-slate-400">
                      M-Sum{' '}
                      {formatClassRange(data.classMedian.minMSum, data.classMedian.maxMSum)}
                    </div>
                  )}
              </div>
            </div>

            <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
              <div className="rounded border border-slate-800 bg-slate-950/50 px-2.5 py-1.5">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">
                  In your class
                </div>
                <div className="tabular-nums text-slate-100">
                  <span className="text-base font-semibold">#{own.classPosition}</span>{' '}
                  <span className="text-slate-500">of {own.classSize}</span>
                </div>
                {own.aheadOfPct != null && (
                  <div className="text-[10px] text-slate-400">
                    ahead of {Math.round(own.aheadOfPct)} %
                  </div>
                )}
              </div>
              <div className="rounded border border-slate-800 bg-slate-950/50 px-2.5 py-1.5">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">
                  Class median
                </div>
                <div className="text-base font-semibold tabular-nums text-slate-100">
                  {formatGrowthPct(data.classMedian?.medianPct ?? null)}
                </div>
                {beatsMedian != null && (
                  <div
                    className={cn(
                      'text-[10px] tabular-nums',
                      beatsMedian >= 0 ? 'text-emerald-300' : 'text-amber-300',
                    )}
                  >
                    you: {beatsMedian >= 0 ? '+' : ''}
                    {beatsMedian.toFixed(1)} pts
                  </div>
                )}
              </div>
            </div>

            <div className="mt-3" style={{ width: '100%', height: 120 }}>
              <ResponsiveContainer>
                <LineChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke="#1e293b" vertical={false} />
                  <XAxis
                    dataKey="label"
                    tick={{ fill: '#64748b', fontSize: 10 }}
                    stroke="#334155"
                    interval="preserveStartEnd"
                    minTickGap={24}
                  />
                  <YAxis
                    tick={{ fill: '#64748b', fontSize: 10 }}
                    stroke="#334155"
                    width={44}
                    domain={[0, 'dataMax']}
                    allowDecimals={false}
                    tickFormatter={(v: number) => (v === 0 ? '0 %' : `+${v} %`)}
                  />
                  <Tooltip
                    contentStyle={{
                      background: '#020617',
                      border: '1px solid #334155',
                      borderRadius: 6,
                      fontSize: 11,
                    }}
                    labelStyle={{ color: '#cbd5e1' }}
                    formatter={(v: unknown) =>
                      v == null ? '—' : formatGrowthPct(Number(v))
                    }
                  />
                  <Line
                    dataKey="Class"
                    stroke="#64748b"
                    strokeWidth={1.5}
                    strokeDasharray="4 4"
                    dot={false}
                    connectNulls
                    isAnimationActive={false}
                  />
                  <Line
                    dataKey="You"
                    stroke={meta.color}
                    strokeWidth={2.5}
                    dot={false}
                    isAnimationActive={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <p className="mt-1 text-[10px] text-slate-500">
              Growth since {own.baselineAt.slice(0, 10)} — you (solid) vs. the
              median of your class (dashed). Classes are thirds of the alliance
              by M-Sum, so you're compared with players of your size.
            </p>
            {own.baselineEstimatedFrom && (
              <p className="mt-1 text-[10px] text-amber-300/80">
                ≈ You didn't report kills around {own.baselineAt.slice(0, 10)}, so
                your start value is estimated between your reports of{' '}
                {own.baselineEstimatedFrom[0].slice(0, 10)} and{' '}
                {own.baselineEstimatedFrom[1].slice(0, 10)}. Report kills weekly
                for exact numbers.
              </p>
            )}
          </>
        )}
      </div>
    </section>
  );
}

const COLOR_KILLS = '#f43f5e'; // rose-500

function KillsChartSection({ history }: { history: SelfReportSummary['history'] }) {
  const killsSeries = useMemo(() => {
    const chron = [...history].reverse();
    // Only keep snapshots where the kill count actually moved — power-only
    // snapshots carry kills forward unchanged and just repeat the value.
    const rows: { label: string; recordedAt: string; Kills: number }[] = [];
    for (const r of chron) {
      if (r.kills <= 0) continue;
      const prev = rows[rows.length - 1];
      if (prev && prev.Kills === r.kills) continue;
      const row = {
        label: r.recordedAt.slice(5, 10),
        recordedAt: r.recordedAt,
        Kills: r.kills,
      };
      // Several snapshots on one day would repeat the date on the axis —
      // keep the day's latest value only.
      if (prev && prev.label === row.label) rows[rows.length - 1] = row;
      else rows.push(row);
    }
    return rows.map((row, i) => ({
      ...row,
      Kills_d: i > 0 ? row.Kills - rows[i - 1].Kills : null,
    }));
  }, [history]);

  if (killsSeries.length < 2) return null;
  return (
    <section className="space-y-2">
      <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-400">
        Kill count over time
      </h2>
      <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
        <div style={{ width: '100%', height: 180 }}>
          <ResponsiveContainer>
            <LineChart
              data={killsSeries}
              margin={{ top: 8, right: 12, left: 0, bottom: 4 }}
            >
              <CartesianGrid stroke="#1e293b" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fill: '#64748b', fontSize: 10 }}
                stroke="#334155"
                interval="preserveStartEnd"
                minTickGap={20}
              />
              <YAxis
                tick={{ fill: '#64748b', fontSize: 10 }}
                stroke="#334155"
                width={36}
                domain={['dataMin', 'dataMax']}
                allowDecimals={false}
              />
              <Tooltip
                contentStyle={{
                  background: '#020617',
                  border: '1px solid #334155',
                  borderRadius: 6,
                  fontSize: 11,
                }}
                labelStyle={{ color: '#cbd5e1' }}
                itemStyle={{ color: '#e2e8f0' }}
                formatter={makeDeltaFormatter(3, 'M')}
              />
              <Line
                type="monotone"
                dataKey="Kills"
                stroke={COLOR_KILLS}
                strokeWidth={2}
                dot={{ r: 2, fill: COLOR_KILLS, stroke: COLOR_KILLS }}
                connectNulls
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <p className="mt-1 text-[10px] text-slate-500">
          Your kill count across {killsSeries.length} snapshots (unchanged
          values hidden).
        </p>
      </div>
    </section>
  );
}

function RankTiles({
  ranks,
}: {
  ranks: NonNullable<SelfReportSummary['ranks']>;
}) {
  const fmtM = (v: number) => v.toFixed(2);
  return (
    <div className="space-y-2">
      <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-400">
        Your alliance rank
      </h2>
      <div className="grid grid-cols-2 gap-2">
        <RankTile label="M1" entry={ranks.m1} format={fmtM} />
        <RankTile label="M2" entry={ranks.m2} format={fmtM} />
        <RankTile label="M3" entry={ranks.m3} format={fmtM} />
        <RankTile label="M-Sum" entry={ranks.mSum} format={fmtM} highlight />
        <div className="col-span-2">
          <RankTile
            label={`M1 · ${ranks.squad.type} squad`}
            entry={ranks.squad}
            format={fmtM}
            variant="squad"
          />
        </div>
        <div className="col-span-2">
          <RankTile
            label="Kills"
            entry={ranks.kills}
            format={formatKills}
            variant="kills"
          />
        </div>
      </div>
    </div>
  );
}

function RankTile({
  label,
  entry,
  format,
  highlight,
  variant,
}: {
  label: string;
  entry: SelfReportRank;
  format: (v: number) => string;
  highlight?: boolean;
  variant?: 'squad' | 'kills';
}) {
  const border = highlight
    ? 'border-indigo-800/70 bg-indigo-900/20'
    : variant === 'squad'
      ? 'border-emerald-800/60 bg-emerald-900/15'
      : variant === 'kills'
        ? 'border-rose-800/60 bg-rose-900/15'
        : 'border-slate-800 bg-slate-900/40';
  return (
    <div className={`rounded-lg border ${border} px-3 py-2`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-400">
        {label}
      </div>
      <div className="mt-0.5 flex items-baseline gap-1">
        <span className="text-lg font-semibold tabular-nums text-slate-100">
          #{entry.rank}
        </span>
        <span className="text-xs text-slate-500 tabular-nums">
          of {entry.total}
        </span>
      </div>
      {(entry.above || entry.below) && (
        <div className="mt-1 space-y-0.5 border-t border-slate-800/80 pt-1">
          {entry.above && (
            <RankNeighborLine dir="above" n={entry.above} format={format} />
          )}
          {entry.below && (
            <RankNeighborLine dir="below" n={entry.below} format={format} />
          )}
        </div>
      )}
    </div>
  );
}

function RankNeighborLine({
  dir,
  n,
  format,
}: {
  dir: 'above' | 'below';
  n: SelfReportRankNeighbor;
  format: (v: number) => string;
}) {
  const above = dir === 'above';
  const name = n.displayName?.trim() || n.name;
  return (
    <div
      className="flex items-center gap-1 text-[10px] text-slate-400"
      title={`${name}: ${format(n.value)} M`}
    >
      {above ? (
        <ChevronUp size={10} className="shrink-0 text-emerald-400" />
      ) : (
        <ChevronDown size={10} className="shrink-0 text-rose-400" />
      )}
      <span className="min-w-0 flex-1 truncate text-slate-300">{name}</span>
      <span
        className={cn(
          'shrink-0 tabular-nums',
          above ? 'text-emerald-300/80' : 'text-rose-300/80',
        )}
      >
        {above ? '+' : '−'}
        {format(n.gap)}
      </span>
    </div>
  );
}

type MetricKey = 'mSum' | 'm1' | 'm2' | 'm3';
const METRIC_LABEL: Record<MetricKey, string> = {
  mSum: 'M-Sum',
  m1: 'M1',
  m2: 'M2',
  m3: 'M3',
};

const COLOR_YOU = '#34d399'; // emerald-400
const COLOR_OTHER = '#334155'; // slate-700
const COLOR_TOP10 = '#fbbf24'; // amber-400
const COLOR_TOP25 = '#38bdf8'; // sky-400
const COLOR_MEDIAN = '#94a3b8'; // slate-400

function fmt(v: number | null | undefined) {
  return v == null ? '—' : v.toFixed(2);
}

function NeighborsSection({
  neighbors,
  metric,
  quips,
}: {
  neighbors: SelfReportSummary['comparison']['neighbors'][MetricKey];
  metric: MetricKey;
  quips: SelfReportSummary['comparison']['chaseQuips'];
}) {
  const { prey, hunter } = neighbors;
  const label = (n: NonNullable<typeof prey>) =>
    n.displayName?.trim() ? n.displayName.trim() : n.name;
  return (
    <div className="space-y-2">
      <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-400">
        The chase · {METRIC_LABEL[metric]}
      </h2>
      <div className="grid gap-2 sm:grid-cols-2">
        {prey ? (
          <div className="rounded-lg border border-emerald-800/60 bg-emerald-950/40 p-3">
            <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-emerald-300">
              <span aria-hidden>🎯</span>
              You are hunting
            </div>
            <div className="mt-1 flex items-baseline justify-between gap-2">
              <span className="truncate text-base font-semibold text-emerald-50">
                {label(prey)}
              </span>
              <span className="shrink-0 text-[10px] uppercase tracking-wider text-emerald-400">
                {prey.rank}
              </span>
            </div>
            <div className="mt-1 flex items-baseline justify-between gap-2 text-xs">
              <span className="text-emerald-300">
                Close the gap:{' '}
                <strong className="text-emerald-100 tabular-nums">
                  {prey.gap.toFixed(2)} Mio
                </strong>
              </span>
              <span className="text-emerald-500 tabular-nums">
                {prey.value.toFixed(1)}
              </span>
            </div>
            <p className="mt-1 text-[10px] italic text-emerald-400/80">
              {quips.prey || 'One good push and you overtake.'}
            </p>
          </div>
        ) : (
          <div className="rounded-lg border border-yellow-800/60 bg-yellow-950/40 p-3">
            <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-yellow-300">
              <span aria-hidden>👑</span>
              Top of the alliance
            </div>
            <p className="mt-1 text-sm text-yellow-100">
              {quips.top || 'Nobody left to chase — hold the throne.'}
            </p>
          </div>
        )}
        {hunter ? (
          <div className="rounded-lg border border-rose-800/60 bg-rose-950/40 p-3">
            <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-rose-300">
              <span aria-hidden>🔥</span>
              Breathing down your neck
            </div>
            <div className="mt-1 flex items-baseline justify-between gap-2">
              <span className="truncate text-base font-semibold text-rose-50">
                {label(hunter)}
              </span>
              <span className="shrink-0 text-[10px] uppercase tracking-wider text-rose-400">
                {hunter.rank}
              </span>
            </div>
            <div className="mt-1 flex items-baseline justify-between gap-2 text-xs">
              <span className="text-rose-300">
                Lead:{' '}
                <strong className="text-rose-100 tabular-nums">
                  {hunter.gap.toFixed(2)} Mio
                </strong>
              </span>
              <span className="text-rose-500 tabular-nums">
                {hunter.value.toFixed(1)}
              </span>
            </div>
            <p className="mt-1 text-[10px] italic text-rose-400/80">
              {quips.hunter || "Keep growing — don't let them pass."}
            </p>
          </div>
        ) : (
          <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
            <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
              <span aria-hidden>🛡️</span>
              Rear guard clear
            </div>
            <p className="mt-1 text-sm text-slate-200">
              {quips.rearClear || 'No one right behind you — plenty of runway.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function DistributionSection({
  comparison,
  metric,
  onMetricChange,
}: {
  comparison: SelfReportSummary['comparison'];
  metric: MetricKey;
  onMetricChange: (m: MetricKey) => void;
}) {
  const setMetric = onMetricChange;
  const values = comparison.distribution[metric];
  const stats = comparison.distribution.stats[metric];
  const ownVal = comparison.distribution.own?.[metric] ?? null;

  const bars = useMemo(() => {
    const sorted = [...values].sort((a, b) => b - a);
    let ownIdx = -1;
    if (ownVal != null) {
      // Anchor own bar to first exact match, else nearest slot by value.
      ownIdx = sorted.findIndex((v) => v === ownVal);
      if (ownIdx === -1) {
        ownIdx = 0;
        let best = Infinity;
        sorted.forEach((v, i) => {
          const d = Math.abs(v - ownVal);
          if (d < best) {
            best = d;
            ownIdx = i;
          }
        });
      }
    }
    return sorted.map((v, i) => ({
      rank: i + 1,
      value: v,
      isYou: i === ownIdx,
    }));
  }, [values, ownVal]);

  const ownRank = bars.find((b) => b.isYou)?.rank ?? null;

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-400">
          Alliance comparison
        </h2>
        <div className="flex gap-1">
          {(['mSum', 'm1', 'm2', 'm3'] as MetricKey[]).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setMetric(k)}
              className={
                'rounded border px-1.5 py-0.5 text-[10px] uppercase tracking-wider ' +
                (metric === k
                  ? 'border-indigo-500 bg-indigo-600/30 text-indigo-100'
                  : 'border-slate-700 bg-slate-950 text-slate-400')
              }
            >
              {METRIC_LABEL[k]}
            </button>
          ))}
        </div>
      </div>
      <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
        <div className="mb-2 text-xs text-slate-400">
          {ownRank != null ? (
            <>
              You are ranked{' '}
              <span className="tabular-nums font-semibold text-emerald-300">
                #{ownRank}
              </span>{' '}
              of {bars.length} on {METRIC_LABEL[metric]} ·{' '}
              <span className="tabular-nums text-emerald-300">
                {fmt(ownVal)}
              </span>{' '}
              Mio
            </>
          ) : (
            <>Alliance {METRIC_LABEL[metric]} distribution</>
          )}
        </div>
        <div style={{ width: '100%', height: 160 }}>
          <ResponsiveContainer>
            <BarChart
              data={bars}
              margin={{ top: 8, right: 8, left: 0, bottom: 4 }}
            >
              <CartesianGrid stroke="#1e293b" vertical={false} />
              <XAxis
                dataKey="rank"
                tick={{ fill: '#64748b', fontSize: 10 }}
                stroke="#334155"
                interval="preserveStartEnd"
              />
              <YAxis
                tick={{ fill: '#64748b', fontSize: 10 }}
                stroke="#334155"
                width={32}
                tickFormatter={(v) => `${v}`}
              />
              <Tooltip
                cursor={{ fill: '#1e293b', opacity: 0.4 }}
                contentStyle={{
                  background: '#020617',
                  border: '1px solid #334155',
                  borderRadius: 6,
                  fontSize: 11,
                }}
                labelStyle={{ color: '#cbd5e1' }}
                itemStyle={{ color: '#e2e8f0' }}
                formatter={(v, _n, p) => [
                  `${Number(v).toFixed(2)} Mio`,
                  (p as { payload?: { isYou?: boolean } })?.payload?.isYou
                    ? 'You'
                    : 'Member',
                ]}
                labelFormatter={(l) => `Rank #${l}`}
              />
              {stats.top10Avg != null && (
                <ReferenceLine
                  y={stats.top10Avg}
                  stroke={COLOR_TOP10}
                  strokeDasharray="4 3"
                  label={{
                    value: `TOP-10 Ø ${stats.top10Avg.toFixed(1)}`,
                    position: 'insideTopRight',
                    fill: COLOR_TOP10,
                    fontSize: 9,
                  }}
                />
              )}
              {stats.top25Avg != null && (
                <ReferenceLine
                  y={stats.top25Avg}
                  stroke={COLOR_TOP25}
                  strokeDasharray="4 3"
                  label={{
                    value: `TOP-25 Ø ${stats.top25Avg.toFixed(1)}`,
                    position: 'insideTopRight',
                    fill: COLOR_TOP25,
                    fontSize: 9,
                  }}
                />
              )}
              {stats.median != null && (
                <ReferenceLine
                  y={stats.median}
                  stroke={COLOR_MEDIAN}
                  strokeDasharray="4 3"
                  label={{
                    value: `Median ${stats.median.toFixed(1)}`,
                    position: 'insideTopRight',
                    fill: COLOR_MEDIAN,
                    fontSize: 9,
                  }}
                />
              )}
              <Bar dataKey="value" radius={[2, 2, 0, 0]}>
                {bars.map((b, i) => (
                  <Cell key={i} fill={b.isYou ? COLOR_YOU : COLOR_OTHER} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-slate-400">
          <LegendDot color={COLOR_YOU} label={`You ${fmt(ownVal)}`} />
          <LegendDot color={COLOR_OTHER} label="Other members" />
          <LegendDot color={COLOR_TOP10} label={`TOP-10 Ø ${fmt(stats.top10Avg)}`} />
          <LegendDot color={COLOR_TOP25} label={`TOP-25 Ø ${fmt(stats.top25Avg)}`} />
          <LegendDot color={COLOR_MEDIAN} label={`Median ${fmt(stats.median)}`} />
        </div>
        <p className="mt-2 text-[10px] text-slate-500">
          Each bar is one active alliance member, sorted from strongest to
          weakest on {METRIC_LABEL[metric]}. Values in Mio.
        </p>
      </div>
    </section>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span className="flex items-center gap-1">
      <span
        className="inline-block h-2 w-2 rounded-full"
        style={{ backgroundColor: color }}
      />
      <span className="tabular-nums">{label}</span>
    </span>
  );
}

function TrendSection({
  trend,
}: {
  trend: SelfReportSummary['comparison']['trend'];
}) {
  const data = trend.map((t, i) => {
    const prev = i > 0 ? trend[i - 1] : null;
    const d = (cur: number | null, before: number | null | undefined) =>
      cur != null && before != null ? cur - before : null;
    return {
      label: t.weekEnd.slice(5),
      weekEnd: t.weekEnd,
      You: t.own,
      'TOP-10 Ø': t.top10Avg,
      Median: t.median,
      You_d: d(t.own, prev?.own ?? null),
      'TOP-10 Ø_d': d(t.top10Avg, prev?.top10Avg ?? null),
      Median_d: d(t.median, prev?.median ?? null),
    };
  });

  return (
    <section className="space-y-2">
      <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-400">
        Trend over time
      </h2>
      <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
        <div style={{ width: '100%', height: 200 }}>
          <ResponsiveContainer>
            <LineChart
              data={data}
              margin={{ top: 8, right: 12, left: 0, bottom: 4 }}
            >
              <CartesianGrid stroke="#1e293b" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fill: '#64748b', fontSize: 10 }}
                stroke="#334155"
                interval="preserveStartEnd"
                minTickGap={20}
              />
              <YAxis
                tick={{ fill: '#64748b', fontSize: 10 }}
                stroke="#334155"
                width={32}
                domain={['dataMin', 'dataMax']}
                allowDecimals={false}
              />
              <Tooltip
                contentStyle={{
                  background: '#020617',
                  border: '1px solid #334155',
                  borderRadius: 6,
                  fontSize: 11,
                }}
                labelStyle={{ color: '#cbd5e1' }}
                itemStyle={{ color: '#e2e8f0' }}
                formatter={makeDeltaFormatter(2, 'Mio')}
              />
              <Line
                type="monotone"
                dataKey="Median"
                stroke={COLOR_MEDIAN}
                strokeWidth={1.5}
                strokeDasharray="4 3"
                dot={false}
                connectNulls
                isAnimationActive={false}
              />
              <Line
                type="monotone"
                dataKey="TOP-10 Ø"
                stroke={COLOR_TOP10}
                strokeWidth={1.5}
                strokeDasharray="4 3"
                dot={false}
                connectNulls
                isAnimationActive={false}
              />
              <Line
                type="monotone"
                dataKey="You"
                stroke={COLOR_YOU}
                strokeWidth={2.5}
                dot={{ r: 3, fill: COLOR_YOU, stroke: COLOR_YOU }}
                activeDot={{ r: 5 }}
                connectNulls
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-slate-400">
          <LegendDot color={COLOR_YOU} label="You" />
          <LegendDot color={COLOR_TOP10} label="TOP-10 Ø" />
          <LegendDot color={COLOR_MEDIAN} label="Median" />
        </div>
        <p className="mt-2 text-[10px] text-slate-500">
          M-Sum over the last {trend.length} weeks. Alliance values carry each
          member's latest confirmed snapshot forward per week.
        </p>
      </div>
    </section>
  );
}

const COLOR_M1 = '#a78bfa';
const COLOR_M2 = '#38bdf8';
const COLOR_M3 = '#f472b6';

type MTrendRange = '30d' | '90d' | '1y' | 'all';
type MTrendMode = 'totals' | 'increase';

const MTREND_RANGE_OPTIONS: { value: MTrendRange; label: string; days: number | null }[] = [
  { value: '30d', label: '30d', days: 30 },
  { value: '90d', label: '90d', days: 90 },
  { value: '1y', label: '1y', days: 365 },
  { value: 'all', label: 'All', days: null },
];

function MTrendSection({ rows }: { rows: SelfReportSummary['history'] }) {
  const [range, setRange] = useState<MTrendRange>('90d');
  const [hideUnchanged, setHideUnchanged] = useState(true);
  const [mode, setMode] = useState<MTrendMode>('totals');

  // History rows come newest-first; chart wants chronological.
  const chronological = useMemo(() => [...rows].reverse(), [rows]);

  const filteredByRange = useMemo(() => {
    const opt = MTREND_RANGE_OPTIONS.find((o) => o.value === range);
    if (!opt?.days) return chronological;
    const cutoff = Date.now() - opt.days * 24 * 60 * 60 * 1000;
    return chronological.filter((r) => new Date(r.recordedAt).getTime() >= cutoff);
  }, [chronological, range]);

  const changedRows = useMemo(() => {
    if (!hideUnchanged) return filteredByRange;
    const out: typeof filteredByRange = [];
    for (let i = 0; i < filteredByRange.length; i++) {
      const r = filteredByRange[i];
      const prev = out[out.length - 1];
      const isFirst = i === 0;
      const isLast = i === filteredByRange.length - 1;
      const unchanged =
        prev &&
        prev.m1Power === r.m1Power &&
        prev.m2Power === r.m2Power &&
        prev.m3Power === r.m3Power;
      if (isFirst || isLast || !unchanged) out.push(r);
    }
    return out;
  }, [filteredByRange, hideUnchanged]);

  const data = useMemo(() => {
    const raw =
      mode === 'totals' || changedRows.length === 0
        ? changedRows.map((r) => ({
            label: r.recordedAt.slice(5, 10),
            recordedAt: r.recordedAt,
            M1: r.m1Power,
            M2: r.m2Power,
            M3: r.m3Power,
          }))
        : (() => {
            // Increase mode: cumulative growth from the earliest snapshot.
            const base = changedRows[0];
            return changedRows.map((r) => ({
              label: r.recordedAt.slice(5, 10),
              recordedAt: r.recordedAt,
              M1: r.m1Power - base.m1Power,
              M2: r.m2Power - base.m2Power,
              M3: r.m3Power - base.m3Power,
            }));
          })();
    return raw.map((row, i) => {
      const prev = i > 0 ? raw[i - 1] : null;
      return {
        ...row,
        M1_d: prev ? row.M1 - prev.M1 : null,
        M2_d: prev ? row.M2 - prev.M2 : null,
        M3_d: prev ? row.M3 - prev.M3 : null,
      };
    });
  }, [changedRows, mode]);

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-400">
          M1–M3 over time
        </h2>
      </div>
      <div className="flex flex-wrap items-center gap-2 text-[11px]">
        <div className="inline-flex overflow-hidden rounded border border-slate-800 bg-slate-900/60">
          {MTREND_RANGE_OPTIONS.map((o) => (
            <button
              key={o.value}
              type="button"
              onClick={() => setRange(o.value)}
              className={
                range === o.value
                  ? 'bg-indigo-600 px-2 py-1 text-white'
                  : 'px-2 py-1 text-slate-400 hover:text-white'
              }
            >
              {o.label}
            </button>
          ))}
        </div>
        <div className="inline-flex overflow-hidden rounded border border-slate-800 bg-slate-900/60">
          <button
            type="button"
            onClick={() => setMode('totals')}
            className={
              mode === 'totals'
                ? 'bg-indigo-600 px-2 py-1 text-white'
                : 'px-2 py-1 text-slate-400 hover:text-white'
            }
          >
            Totals
          </button>
          <button
            type="button"
            onClick={() => setMode('increase')}
            className={
              mode === 'increase'
                ? 'bg-indigo-600 px-2 py-1 text-white'
                : 'px-2 py-1 text-slate-400 hover:text-white'
            }
          >
            Increase
          </button>
        </div>
        <label className="inline-flex cursor-pointer items-center gap-1.5 rounded border border-slate-800 bg-slate-900/60 px-2 py-1 text-slate-300">
          <input
            type="checkbox"
            checked={hideUnchanged}
            onChange={(e) => setHideUnchanged(e.target.checked)}
            className="accent-indigo-500"
          />
          Hide unchanged
        </label>
      </div>
      <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
        {data.length < 2 ? (
          <div className="py-8 text-center text-xs text-slate-500">
            Not enough snapshots in this range.
          </div>
        ) : (
          <div style={{ width: '100%', height: 200 }}>
            <ResponsiveContainer>
              <LineChart
                data={data}
                margin={{ top: 8, right: 12, left: 0, bottom: 4 }}
              >
                <CartesianGrid stroke="#1e293b" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fill: '#64748b', fontSize: 10 }}
                  stroke="#334155"
                  interval="preserveStartEnd"
                  minTickGap={20}
                />
                <YAxis
                  tick={{ fill: '#64748b', fontSize: 10 }}
                  stroke="#334155"
                  width={32}
                  domain={['dataMin', 'dataMax']}
                  allowDecimals={false}
                />
                <Tooltip
                  contentStyle={{
                    background: '#020617',
                    border: '1px solid #334155',
                    borderRadius: 6,
                    fontSize: 11,
                  }}
                  labelStyle={{ color: '#cbd5e1' }}
                  itemStyle={{ color: '#e2e8f0' }}
                  formatter={makeDeltaFormatter(2, 'Mio')}
                />
                <Line
                  type="monotone"
                  dataKey="M1"
                  stroke={COLOR_M1}
                  strokeWidth={2}
                  dot={{ r: 2, fill: COLOR_M1, stroke: COLOR_M1 }}
                  connectNulls
                  isAnimationActive={false}
                />
                <Line
                  type="monotone"
                  dataKey="M2"
                  stroke={COLOR_M2}
                  strokeWidth={2}
                  dot={{ r: 2, fill: COLOR_M2, stroke: COLOR_M2 }}
                  connectNulls
                  isAnimationActive={false}
                />
                <Line
                  type="monotone"
                  dataKey="M3"
                  stroke={COLOR_M3}
                  strokeWidth={2}
                  dot={{ r: 2, fill: COLOR_M3, stroke: COLOR_M3 }}
                  connectNulls
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-slate-400">
          <LegendDot color={COLOR_M1} label="M1" />
          <LegendDot color={COLOR_M2} label="M2" />
          <LegendDot color={COLOR_M3} label="M3" />
        </div>
        <p className="mt-2 text-[10px] text-slate-500">
          {mode === 'increase'
            ? `Growth of M1/M2/M3 since the first snapshot in this range (${data.length} shown${hideUnchanged ? ', unchanged snapshots hidden' : ''}).`
            : `Your M1/M2/M3 across ${data.length} snapshot${data.length === 1 ? '' : 's'}${hideUnchanged ? ' (unchanged snapshots hidden)' : ''}.`}
        </p>
      </div>
    </section>
  );
}

function makeDeltaFormatter(decimals: number, unit: string) {
  return (value: unknown, name: unknown, item: unknown): string => {
    const dataKey = (item as { dataKey?: string })?.dataKey;
    const payload = (item as { payload?: Record<string, unknown> })?.payload;
    const delta = dataKey && payload ? payload[`${dataKey}_d`] : undefined;
    void name;
    const base =
      value == null ? '—' : `${Number(value).toFixed(decimals)} ${unit}`;
    if (delta == null) return base;
    const d = Number(delta);
    const sign = d > 0 ? '+' : '';
    return `${base}  (${sign}${d.toFixed(decimals)})`;
  };
}
